<template>
  <ShipmentMap
    :id="mapId"
    :key="`map-${shipment?.id}`"
    :shipment="shipment"
    :static-map="staticMapUrl.data"
    :loading="staticMapUrl.loading"
    :dynamic="showDynamicMap"
    :interactive="isMapActionsEnabled"
    :events="shipmentEvents"
    :route-geo-json="routeGeoJson.data"
  />
  <Content
    :audit-log-modal-label="
      t('shipments.shipmentNumParam', { param: shipmentId })
    "
    :audit-records="auditRecords"
    :action-records="actionRecords"
    :created-audit-record="createdAuditRecord"
    :subheader="
      isOwningClientSubOfCurrentDomain
        ? t('shipments.detailsForShipmentId', { shipmentId })
        : undefined
    "
    :loading="loading || saving"
    :back-btn-route="{ name: 'shipmentList', query: { search: searchParams } }"
    :back-btn-label="t('shipments.backToAllShipments')"
    :show-toolbar="shipment.status !== ShipmentStatus.CANCELED && isAnyDirty"
    class="shipment-detail"
    header-dark-bg
    remove-card-padding
    remove-aside-card-padding
    @show-audit-log="loadActionAndAuditRecords"
  >
    <template #headerLeft>
      <ClientAvatarAndName
        v-if="isOwningClientSubOfCurrentDomain"
        :accent-color="clientBranding.data?.accentColor"
        :name="shipment.owner?.name"
        :logo="clientBranding.data?.avatar?.signedUrl"
        size="large"
      >
        <template #name="{ value }">
          <h1 id="content-heading" tabindex="-1">{{ value }}</h1>
        </template>
      </ClientAvatarAndName>
      <template v-else>
        <HeaderIcon icon="map-marked-alt" />
        <h1 id="content-heading" tabindex="-1">
          {{ t('shipments.detailsForShipmentId', { shipmentId }) }}
        </h1>
      </template>

      <TagContainer
        v-model="internalTags"
        :loading="tagOptions.loading"
        :options="tagOptions.data"
        :readonly="!$hasAuthority('shipment', 'update')"
        :route-builder="buildTagSearchRoute"
        :allow-deletes="canDeleteTags"
        @add="onAddTag"
        @remove="onRemoveTag"
        @delete="
          requestDeleteConfirmation({
            message: t('tags.tagWarningParam', {
              param: t('common.ordersAndShipments'),
            }),
            accept: () => onDelete($event),
          })
        "
      />

      <AssignedUserContainer
        id="assigned-user-container"
        v-model="shipment.assignedUsers"
        :options="assignableUsers"
        :loading="loading"
        :disabled="!$hasAuthority('shipment', 'update')"
        class="assign-user-container"
        @update:model-value="saveAssignedUsers"
      />
    </template>

    <template #headerRight>
      <div class="flex flex-gap-1 align-items-center">
        <ExpandButton
          :model-value="isMapExpanded"
          :loading="routeGeoJson.loading || staticMapUrl.loading"
          class="map-details-button p-button-text text-white flex"
          :target-id="mapId"
          :true-label="t('maps.hideDetails')"
          :false-label="t('maps.showDetails')"
          @update:model-value="onMapDetailsButtonClick"
        />
        <ShipmentActionButton
          :shipment="shipment"
          :shipment-events="shipmentEvents"
          :carrier-settings="carrierSettings"
          :accepted-rate-quote="acceptedRateQuote"
          :waterfall-tender="waterfallTender.data"
          :loading="loading || bolTemplateOptions.loading"
          :printing="isPrinting"
          :carrier-direct-credentials-present="carrierDirectCredentialsPresent"
          :macro-point-credentials-present="macroPointCredentialsPresent"
          :cargo-insurance-status="
            shipmentCargoInsuranceCertificate.data?.status
          "
          data-testid="shipment-detail-action-button"
          @command="onShipmentActionCommand"
        />
      </div>
    </template>

    <template #toolbar>
      <EditBar
        :loading="loading"
        :saving="saving"
        @save="saveShipment"
        @cancel="loadShipment(shipmentId)"
      />
    </template>

    <template #aside>
      <RelatedOrdersSection
        v-if="$hasAuthority('order', 'read')"
        id="related-orders"
        :orders="relatedOrders"
      />

      <ShipmentManifestSection
        v-if="isParcelManifestingEnabled"
        id="shipment-manifest"
        :manifest="manifest"
      />

      <ReferenceNumbersSection
        id="reference-numbers"
        v-model="shipmentLevelReferenceNumbers"
        :resource-id="shipmentId"
        :level="ReferenceNumberTypeLevel.SHIPMENT"
        :editable="
          $hasAuthority('referenceData', 'read') &&
          $hasAuthority('shipment', 'update') &&
          !shipmentEditingLocked
        "
        @change="numDirtyRefNums = $event.numDirty"
      />

      <RelatedReferenceNumberSection
        id="additional-reference-numbers"
        :orders="relatedOrders"
        :editable="
          $hasAuthority('referenceData', 'read') &&
          $hasAuthority('order', 'update') &&
          !shipmentEditingLocked
        "
      />

      <RelatedGLCodeSection
        id="related-gl-codes"
        :orders="relatedOrders.data"
        :loading="relatedOrders.loading"
        :editable="$hasAuthority('order', 'update') && !shipmentEditingLocked"
      />

      <AccessorialSection
        id="accessorials"
        ref="accessorialSection"
        v-model:top-level-accessorials="shipment.requestedAccessorials"
        v-model:stop-level-accessorials="stopLevelAccessorials"
        :editable="
          $hasAuthority('shipment', 'update') &&
          $hasAuthority('referenceData', 'read') &&
          !shipmentEditingLocked
        "
        @change="numDirtyAccessorials = $event.numDirty"
      />

      <BillToSection
        id="bill-to"
        v-model:bill-to="v$.billTo.$model"
        :editable="userCanEditShipment"
      />

      <DriverContactSection
        id="driver-contact"
        :editable="
          $hasAuthority('shipment', 'updateLockedFields') &&
          !shipmentEditingLocked
        "
      />

      <NoteSection
        id="notes"
        v-model:internal-notes="v$.internalNotes.$model"
        v-model:carrier-notes="v$.carrierNotes.$model"
        :editable="
          $hasAuthority('shipment', 'update') && !shipmentEditingLocked
        "
      />

      <DocumentsSection
        v-if="$hasAuthority('document', 'read')"
        id="documents"
        :editable="
          $hasAuthority('shipment', 'update') &&
          $hasAuthority('document', 'create') &&
          !shipmentEditingLocked
        "
      />

      <RelatedDocumentSection
        v-if="$hasAuthority('document', 'read')"
        id="related-documents"
      />

      <ValueOfGoodsSection
        id="value-of-goods"
        v-model:value-of-goods="v$.valueOfGoods.$model"
        v-model:line-items="v$.lineItems.$model"
        :editable="userCanEditShipment"
      />
      <ShipmentWorkflowRulesSection
        v-if="appliedWorkflowRules.length"
        :workflow-rules="appliedWorkflowRules"
      />
    </template>

    <template v-if="isFinancialsKillSwitchEnabled" #secondaryAside>
      <ShipmentFinancialsSection
        v-if="$hasAuthority('shipmentFinancials', 'readShipmentLedger')"
        id="financials"
        ref="shipmentFinancialsSection"
        :shipment="shipment"
        :related-orders="relatedOrders.data"
        :can-view-uplift="canViewUplift"
      />
    </template>

    <BlockUI :blocked="saving" full-screen />
    <section
      id="highlights"
      :aria-label="t('common.highlights', 2)"
      class="detail-section shipment-detail-header flex m-0 p-0"
      tabindex="-1"
    >
      <div class="shipment-detail-header-cell w-3">
        <KeyValuePair
          :label="t('common.paramId', { param: t('shipments.shipments') })"
          :value="shipment.id"
          juicy
        />
      </div>

      <div class="shipment-detail-header-cell w-3">
        <KeyValuePair :label="t('common.status')" :value="statusTooltip" juicy>
          <template #value>
            <SwcChip v-if="shipment.swc" />
            <ShipmentStatusChip v-else :status="shipment.status" />
          </template>
        </KeyValuePair>
      </div>

      <div class="shipment-detail-header-cell shipment-health-cell w-3">
        <KeyValuePair
          :label="t('shipments.health')"
          :value="
            shipment.health
              ? translateEnum(shipment.health, 'ShipmentHealth')
              : '-'
          "
          juicy
        >
          <template #value>
            <ShipmentHealthChip
              v-model:health="v$.health.$model"
              :editable="
                $hasAuthority('shipment', 'update') && !shipmentEditingLocked
              "
            />
          </template>
        </KeyValuePair>
      </div>

      <div class="shipment-detail-header-cell w-3">
        <KeyValuePair
          :label="t('carriers.carriers')"
          :value="shipment?.carrierName"
          juicy
        />
      </div>
    </section>

    <ShipmentBasicInformationSection
      id="basic-information"
      v-model="shipment"
      :shipment-service-incidents="shipmentServiceIncidents"
      :service-incident-types="serviceIncidentTypes.data ?? []"
      :editable="userCanEditShipment"
      @scroll-trigger="onScrollToTimeline"
    />

    <LocationSection
      :id="LOCATION_SECTION_ID"
      ref="locationSection"
      v-model="v$.stops.$model"
      :line-items="v$.lineItems.$model"
      :errors="v$.stops.$errors"
      :editable="userCanEditShipment"
      @change="numDirtyStops = $event.numDirty"
      @update:line-items="shipment.lineItems = $event"
    />

    <ShipmentRateShop
      v-if="$hasAuthority('rateShop', 'read')"
      id="rate-shop"
      ref="shipmentRateShop"
      v-model:freight-allowance="shipment.freightAllowance"
      :shipment="shipment"
      :rate-shop="rateShop"
      :financial-status="shipment?.financialSummary?.financialStatus"
      :brokerage-dates="brokerageDates"
      :carrier-settings="carrierSettings"
      :waterfall-tender="waterfallTender.data"
      :estimated-mileage="shipment.estimatedMileage"
      :rate-shop-loading="rateShopLoading"
      :disabled="rateShopLoading || shipmentEditingLocked"
      :view-uplift="canViewUplift"
      :cargo-insurance-status="shipmentCargoInsuranceCertificate.data?.status"
      @command="onShipmentActionCommand"
      @edit-brokerage-quote="onBrokerageEdit"
      @rate-quote-updated="onQuoteUpdated"
      @rate-selected="onRateQuoteSelected"
      @rate-shop-completed="onRateShopCompleted"
      @request-spot-market-bid="confirmShowInviteCarriersToSpotMarket"
      @send-to-brokerage="onSendToBrokerage"
      @spot-quote-added="onSpotQuoteAdded"
      @spot-quote-deleted="onSpotQuoteDeleted"
      @update-rates="onUpdateRates"
      @waterfall-tender="waterfallTenderModalShow"
    />
    <ShipmentRateShopAcceptedRateSummary
      v-else-if="shipment && !$hasAuthority('rateShop', 'read')"
      id="rate-shop"
      v-model:freight-allowance="shipment.freightAllowance"
      :rate-shop="rateShop"
      :shipment="shipment"
      :loading="loading"
      :financial-status="shipment.financialSummary?.financialStatus"
    />

    <ShipmentTenderInfo
      v-if="tenderRequest.data?.shipmentId"
      id="tender"
      :tender-request="tenderRequest.data"
      :loading="tenderRequest.loading"
    />

    <ShipmentCargoInsuranceSection
      v-if="shipmentCargoInsuranceCertificate.data"
      id="cargo-insurance"
      :cargo-insurance-certificate="shipmentCargoInsuranceCertificate"
      @command="onShipmentActionCommand"
    />

    <ShipmentEventSection
      id="events"
      ref="shipmentEventSection"
      :shipment="shipment"
      :shipment-events="shipmentEvents"
      :shipment-service-incidents="shipmentServiceIncidents"
      :tender-requests="tenderRequests"
      :service-incident-types="serviceIncidentTypes"
      @shipment-event-or-service-incident-saved="
        onShipmentEventOrServiceIncidentSaved
      "
    />

    <LineItemSection
      id="line-items"
      ref="lineItemSection"
      v-model="v$.lineItems.$model"
      :errors="v$.lineItems.$errors"
      :stop-filter="selectedStop"
      :show-stops="shipment.stops.length > 2"
      :editable="userCanEditShipment"
      @change="numDirtyLineItems = $event.numDirty"
      @remove="onLineItemRemoved"
    />
  </Content>

  <ResendConfirmationModal
    v-model:visible="resendConfirmationModal.isVisible"
    :carrier-type="shipment.carrierType"
    :emails="resendConfirmationModal.emails"
    @confirm-resend-confirmation-clicked="resendConfirmation"
  />

  <PrintShippingLabelTemplateModal
    v-model:visible="printShippingLabelTemplateModal.isVisible"
    :attached-labels="shippingLabelDocuments"
    @confirm="printShippingLabelsFromTemplate"
    @print-attached="printAttachedShippingLabel"
  />

  <TrackingLinkModal
    v-model:visible="sharingModal.isVisible"
    :shipment-id="shipment.id"
    :orders="relatedOrdersSharingUrls"
    @share-via-email="sendSharingEmails"
  />

  <ShipmentCancelModal
    v-model:visible="cancelShipmentModal.isVisible"
    :shipment="shipment"
    :carrier-notification-error-message="
      cancelShipmentModal.carrierNotificationErrorMessage
    "
    :requires-manual-cancellation="requiresManualCancellation"
    :loading="cancelShipmentModal.loading"
    @cancel-shipment="onConfirmCancel"
  />

  <CargoInsuranceCancellationModal
    v-model:visible="cargoInsuranceCancellationModal.isVisible"
    @cancel-cargo-insurance="onCargoInsuranceCancelConfirm"
  />

  <DocumentEmailModal v-model:visible="emailDocumentsModalVisible" />

  <DocumentRequestResultModal
    v-model:visible="documentRequestResultModal.isVisible"
    :responses="documentRequestResultModal.response"
  />

  <WaterfallTenderModal
    v-if="
      isWaterfallTenderingEnabled &&
      shipment.id &&
      shipment.status === ShipmentStatus.PLANNING
    "
    v-model:visible="waterfallTenderModalVisible"
    :rate-shop="rateShop"
    @waterfall-tender="onWaterfallTender"
  />

  <MacroPointAdditionalInfoModal
    v-model:visible="macroPointAdditionalInfoModal.isVisible"
    :shipment-id="macroPointAdditionalInfoModal.shipmentId"
    :required-reference-numbers="
      macroPointAdditionalInfoModal.requiredReferenceNumbers
    "
    :existing-reference-numbers="shipmentLevelReferenceNumbers"
    @update:values="addAdditionalInfoAndInitiateMacroPointTracking"
  />

  <RemoveOrdersModal
    v-model:visible="showRemoveOrdersModal"
    :related-orders="relatedOrders"
    :shipment="shipment"
    @remove="removeOrders"
  />

  <PurchaseCargoInsuranceModal
    v-model:visible="purchaseCargoInsuranceModal.isVisible"
    :shipment="shipment"
    @purchased="onCargoInsurancePurchased"
  />

  <EmailCheckCallRequestsModal
    v-model:visible="sendCheckCallRequestsModal.isVisible"
    :emails="sendCheckCallRequestsModal.emails"
    @submit="sendCheckCallEmails(shipmentId, $event)"
  />

  <InviteCarriersToSpotMarketModal
    v-model:visible="inviteCarriersToSpotMarketModal.visible"
    :invite-command="inviteCarriersToSpotMarketModal.inviteCommand"
    :carrier-notes="shipment.carrierNotes"
    :can-view-uplift="canViewUplift"
    show-carrier-notes
    @submit="onInviteCarriersToSpotMarket"
  />

  <SpotMarketErrorModal
    v-model:visible="spotMarketErrorModal.visible"
    :spot-markets="spotMarketErrorModal.erroredSpotMarkets"
  />

  <RateAssignAndTenderModal
    v-model:visible="rateSelectAndTenderModal.visible"
    :rate-quote="rateSelectAndTenderModal.rateQuote"
    :active-carrier-setting="carrierSettings"
    :tender-request="tenderRequest.data"
    :is-tender-only="rateSelectAndTenderModal.tenderOnly"
    @assign-and-tender="onAcceptRateQuoteAndTenderToCarrier"
    @assign-rate-only="onRateQuoteAssigned"
    @tender-to-carrier-only="onTenderToCarrier"
  />

  <EditBrokerageQuoteRedesignModal
    v-if="arApFeatureFlagEnabled"
    v-model:visible="brokerageInfo.isShowingModal"
    :brokerage-info="brokerageInfo"
    :brokerage-dates="brokerageDates"
    is-brokered-from-shipment
    :view-uplift="canViewUplift"
    @confirm="onBrokerageConfirmed"
  />
  <BrokerageModal
    v-else
    v-model:visible="brokerageInfo.isShowingModal"
    :brokerage-info="brokerageInfo"
    :brokerage-dates="brokerageDates"
    is-brokered-from-shipment
    @confirm="onBrokerageConfirmed"
  />

  <RateSelectionReasonModal
    v-model:visible="lowestCostReasonModal.visible"
    :lowest-price-quote="lowestPriceQuote"
    :selected-quote="lowestCostReasonModal.selectedQuote"
    @confirm="onRateSelectionConfirmed"
  />

  <LineItemRemovedModal
    v-model:visible="lineItemRemovedModal.visible"
    :removed-line-item="lineItemRemovedModal.removedLineItem"
    :is-ship-order-split="lineItemRemovedModal.isShipOrderSplit"
    @update:visible="loadShipment(shipmentId)"
    @confirm="onLineItemRemovalConfirmed($event)"
    @cancel="loadShipment(shipmentId)"
  />

  <BolTotalHandlingUnitOverrideModal
    v-model:visible="isBolTotalHandlingUnitOverrideModalVisible"
    @override="onBolTotalHandlingUnitOverride"
    @skip="printBol"
  />

  <PrintBolTemplateModal
    v-model:visible="isBolTemplateModalVisible"
    :templates="bolTemplateOptions.data?.templates"
    :matched-template-id="bolTemplateOptions.data?.matchedTemplateId"
    @confirm="printBol"
  />
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { helpers } from '@vuelidate/validators';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  toRef,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import {
  minLength,
  required,
  useDeleteConfirmation,
  useScrollAnimations,
  useToast,
  useUid,
  useUnsavedChangesConfirmation,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import {
  AccessorialCode,
  ActionCommand,
  CapacityProviderType,
  CarrierSetting,
  CorsairDocumentType,
  ErrorResponse,
  Loadable,
  ReferenceNumberTypeLevel,
  ServiceIncidentType,
  SharingEmailType,
  Tag,
  TenderConfig,
  TenderConfigOptions,
  TenderMethod,
  UserRef,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  useActionLog,
  useDocuments,
  useKillSwitchFlag,
  useLowestCostReasonCode,
  useMap,
  useRateShop,
  useRecentlyViewed,
  useShipmentRateShop,
  useShipmentStops,
  useShipmentWorkflowRules,
  useSpotMarketPolling,
} from '@/composables';
import {
  AcceptQuotePayload,
  BrokerageDates,
  BrokeragePayload,
  CancelShipmentPayload,
  CargoInsuranceBadRequestResponse,
  CargoInsuranceCancellationRequest,
  CargoInsuranceCertificate,
  DefaultOrderData,
  DocumentRequestResponse,
  DocumentTemplateMatch,
  DocumentType,
  Manifest,
  Order,
  P44_RATE_STRATEGIES,
  RateQuote,
  RateSelectionReason,
  ReferenceNumber,
  SharingRequest,
  Shipment,
  ShipmentAction,
  ShipmentEvent,
  ShipmentInviteCarriersToSpotMarketCommand,
  ShipmentLineItem,
  ShipmentServiceIncident,
  ShipmentStatus,
  ShipmentStop,
  ShippingLabelFormat,
  SignedDocument,
  SpotMarket,
  TenderAcceptRequest,
  TenderRejectRequest,
  TenderRequest,
  TenderResponseCode,
  TenderToCarrierRequest,
  WaterfallTender,
  WaterfallTenderConfig,
  WellKnownRateStrategy,
} from '@/model';
import { useAssignableUserStore } from '@/stores/AssignableUser';
import { useCheckCallStore } from '@/stores/CheckCall';
import { useCreditUsageStore } from '@/stores/CreditUsage';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { useSpotMarketStore } from '@/stores/SpotMarket';
import { useTagStore } from '@/stores/Tag';
import {
  buildSearchRoute,
  getFlattenedFieldErrors,
  isBadRequest,
  isStatusEqualOrAfter,
  isStatusEqualOrBefore,
} from '@/util';
import {
  deliverShippingLabel,
  openAttachedShippingLabel,
  shippingLabelErrorDetail,
  sortShippingLabelsNewestFirst,
} from '@/util/ShippingLabelUtil';

import AccessorialSection from '@/components/accessorial/AccessorialSection.vue';
import CargoInsuranceCancellationModal from '@/components/cargoInsurance/CargoInsuranceCancellationModal.vue';
import PurchaseCargoInsuranceModal from '@/components/cargoInsurance/PurchaseCargoInsuranceModal.vue';
import InviteCarriersToSpotMarketModal from '@/components/carrierPortal/InviteCarriersToSpotMarketModal.vue';
import SpotMarketErrorModal from '@/components/carrierPortal/SpotMarketErrorModal.vue';
import ClientAvatarAndName from '@/components/client/ClientAvatarAndName.vue';
import DocumentEmailModal from '@/components/document/DocumentEmailModal.vue';
import DocumentsSection from '@/components/document/DocumentsSection.vue';
import RelatedDocumentSection from '@/components/document/RelatedDocumentSection.vue';
import RelatedGLCodeSection from '@/components/glCode/RelatedGLCodeSection.vue';
import LineItemRemovedModal from '@/components/lineItem/LineItemRemovedModal.vue';
import LineItemSection from '@/components/lineItem/LineItemSection.vue';
import BillToSection from '@/components/location/BillToSection.vue';
import LocationSection from '@/components/location/LocationSection.vue';
import NoteSection from '@/components/note/NoteSection.vue';
import RateAssignAndTenderModal from '@/components/rateShop/assignAndTender/RateAssignAndTenderModal.vue';
import BrokerageModal from '@/components/rateShop/BrokerageModal.vue';
import EditBrokerageQuoteRedesignModal, {
  type BrokerageInfo,
} from '@/components/rateShop/editQuote/EditBrokerageQuoteRedesignModal.vue';
import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import WaterfallTenderModal from '@/components/rateShop/WaterfallTenderModal.vue';
import ReferenceNumbersSection from '@/components/referenceNumber/ReferenceNumbersSection.vue';
import RelatedReferenceNumberSection from '@/components/referenceNumber/RelatedReferenceNumberSection.vue';
import RelatedOrdersSection from '@/components/relatedOrders/RelatedOrdersSection.vue';
import RemoveOrdersModal from '@/components/removeOrders/RemoveOrdersModal.vue';
import BolTotalHandlingUnitOverrideModal from '@/components/shipment/BolTotalHandlingUnitOverrideModal.vue';
import DocumentRequestResultModal from '@/components/shipment/DocumentRequestResultModal.vue';
import DriverContactSection from '@/components/shipment/driverContact/DriverContactSection.vue';
import EmailCheckCallRequestsModal from '@/components/shipment/EmailCheckCallRequestsModal.vue';
import MacroPointAdditionalInfoModal from '@/components/shipment/MacroPointAdditionalInfoModal.vue';
import ShipmentManifestSection from '@/components/shipment/manifest/ShipmentManifestSection.vue';
import PrintBolTemplateModal from '@/components/shipment/PrintBolTemplateModal.vue';
import PrintShippingLabelTemplateModal from '@/components/shipment/PrintShippingLabelTemplateModal.vue';
import ShipmentRateShopAcceptedRateSummary from '@/components/shipment/rateShop/ShipmentRateShopAcceptedRateSummary.vue';
import ResendConfirmationModal from '@/components/shipment/ResendConfirmationModal.vue';
import ShipmentActionButton from '@/components/shipment/ShipmentActionButton.vue';
import ShipmentBasicInformationSection from '@/components/shipment/ShipmentBasicInformationSection.vue';
import ShipmentCancelModal from '@/components/shipment/ShipmentCancelModal.vue';
import ShipmentCargoInsuranceSection from '@/components/shipment/ShipmentCargoInsuranceSection.vue';
import ShipmentEventSection from '@/components/shipment/ShipmentEventSection.vue';
import ShipmentHealthChip from '@/components/shipment/ShipmentHealthChip.vue';
import ShipmentMap from '@/components/shipment/ShipmentMap.vue';
import ShipmentRateShop from '@/components/shipment/ShipmentRateShop.vue';
import ShipmentStatusChip from '@/components/shipment/ShipmentStatusChip.vue';
import ShipmentTenderInfo from '@/components/shipment/ShipmentTenderInfo.vue';
import ShipmentWorkflowRulesSection from '@/components/shipment/ShipmentWorkflowRulesSection.vue';
import TrackingLinkModal from '@/components/shipment/tracking/TrackingLinkModal.vue';
import ShipmentFinancialsSection from '@/components/shipmentFinancials/ShipmentFinancialsSection.vue';
import EditBar from '@/components/ui/EditBar.vue';
import ValueOfGoodsSection from '@/components/valueOfGoods/ValueOfGoodsSection.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import HeaderIcon from '@rygen/rygen-ui/components/content/HeaderIcon.vue';
import AssignedUserContainer from '@rygen/rygen-ui/components/form/AssignedUserContainer.vue';
import SwcChip from '@rygen/rygen-ui/components/swc/SwcChip.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import BlockUI from 'primevue/blockui';

const LOCATION_SECTION_ID = 'locations';

type DocumentRequestResultModalData = {
  isVisible: boolean;
  response: DocumentRequestResponse[];
};

const newBrokerageQuoteData = (
  rateQuote?: RateQuote,
  rateSelectionReason?: RateSelectionReason,
): BrokerageInfo => ({
  data: new RateQuote({
    ...rateQuote,
    source: 'BROKERAGE',
  }),
  dates: new BrokerageDates(),
  isShowingModal: false,
  shipmentSpecId: undefined,
  rateSelectionReason: rateSelectionReason,
  loading: false,
  error: undefined,
});

const props = defineProps({
  shipmentId: {
    type: Number,
    required: true,
  },
});
const { shipmentId } = toRefs(props);

const shipment: Ref<Shipment> = ref(new Shipment());

const arApFeatureFlagEnabled = useFlag('ar-ap-split-feat-flag');
const isFinancialsKillSwitchEnabled = useKillSwitchFlag(
  'tms-shipment-financials-kill-switch',
);
const isShipmentLineItemRemoveShipOrderEnabled = useFlag(
  'shipment-line-item-remove-ship-order-feat-flag',
);
const isWaterfallTenderingEnabled = useFlag('waterfallTendering');
const isJasperBolEnabled = useFlag('jasper-reports-bol-feat-flag');
const isBolHandlingUnitOverrideEnabled = useFlag(
  'bol-total-handling-unit-override-feat-flag',
);
const isZplShippingLabelEnabled = useFlag('zpl-shipping-label-feat-flag');
const isParcelManifestingEnabled = useFlag('parcel-manifest-feat-flag');

const accessorialSection = useTemplateRef('accessorialSection');
const lineItemSection = useTemplateRef('lineItemSection');
const locationSection = useTemplateRef('locationSection');
const shipmentFinancialsSection = useTemplateRef('shipmentFinancialsSection');
const shipmentEventSection = useTemplateRef('shipmentEventSection');
const shipmentRateShop = useTemplateRef('shipmentRateShop');

const {
  actionRecords,
  auditRecords,
  createdAuditRecord,
  loadActionAndAuditRecords,
} = useActionLog(shipment);
const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const { cancelCheckCallEmails, loadCheckCallStatus, sendCheckCallEmails } =
  useCheckCallStore();
const confirm = useConfirm();
const { handleRequiredDomainRedirect } = useCurrentUserStore();
const { performTenderAuthorization } = useCreditUsageStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { documents } = useDocuments();
const { t } = useI18n();
const {
  isMapActionsEnabled,
  isMapExpanded,
  mapId,
  onMapDetailsButtonClick,
  refreshRouteGeoJson,
  refreshStaticMapUrl,
  routeGeoJson,
  showDynamicMap,
  staticMapUrl,
} = useMap(shipment);
const {
  clearParentEntity,
  clientBranding,
  isOwningClientSubOfCurrentDomain,
  loadClientBranding,
  owningClient,
  setParentEntity,
} = useParentEntityStore();
useRecentlyViewed(shipment);
const router = useRouter();
const { scrollIntoView } = useScrollAnimations();
const {
  acceptRateQuote,
  acceptedRateQuote,
  addBrokerageQuote,
  addSpotQuote,
  deleteSpotQuote,
  loadRateShop,
  rateShop,
  rateShopLoading,
  updateBrokerageQuote,
  updateRateQuote,
  updateRates,
} = useShipmentRateShop(shipment);
const { appliedWorkflowRules } = useShipmentWorkflowRules(shipment);
const { inviteCarriersToSpotMarket, loadSpotMarkets } = useSpotMarketStore();
const { startSpotMarketPollingForResult } = useSpotMarketPolling();
const { loadTags, tags, deleteTag, tagOptions } = useTagStore();
const toast = useToast();
const { uid } = useUid('shipment-detail');
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();

const { lowestPriceQuote } = useRateShop(rateShop, {});

const shipmentEvents: Ref<ShipmentEvent[]> = ref([]);
const {
  firstUnresolvedDropOffMilestone,
  firstUnresolvedPickupMilestone,
  lastResolvedDropOffStop,
  lastResolvedPickupStop,
} = useShipmentStops(shipment, shipmentEvents);

const getOrphanedStops = (stops: ShipmentStop[]): ShipmentStop[] =>
  stops.filter(
    (stop) =>
      !shipment.value.lineItems.some(
        (l) =>
          l.pickupStopSequenceNumber === stop.sequenceNumber ||
          l.dropOffStopSequenceNumber === stop.sequenceNumber,
      ),
  );

const rules = {
  lineItems: {
    required: required(t('lineItems.lineItems')),
    minLength: minLength(1, t('lineItems.lineItems', 2)),
    allStopsAssignedToLineItem: helpers.withMessage(
      t('shipments.errors.orphanedStopTooltip'),
      () => getOrphanedStops(shipment.value.stops).length === 0,
    ),
  },
  referenceNumbers: {},
  requestedAccessorials: {},
  internalNotes: {},
  carrierNotes: {},
  health: {},
  origin: {},
  destination: {},
  billTo: {},
  stops: {
    allAssignedToLineItem: helpers.withMessage(
      t('shipments.errors.orphanedStopTooltip'),
      (stops: ShipmentStop[]) => getOrphanedStops(stops).length === 0,
    ),
  },
  freightAllowance: {},
  valueOfGoods: {},
};
const { resetModel, setServerErrors, validate, v$ } = useValidations(
  shipment,
  rules,
  uid,
);

useUnsavedChangesConfirmation({
  isDirtyCallback: async () => isAnyDirty.value,
});

const isPrinting = ref(false);
const canViewUplift = ref(false);
const isBolTotalHandlingUnitOverrideModalVisible = ref(false);
const isBolTemplateModalVisible = ref(false);
const bolTemplateOptions = ref<Loadable<DocumentTemplateMatch>>({
  loading: true,
});
const saving = ref(false);
const loading = ref(false);
const carrierDirectCredentialsPresent = ref(false);
const macroPointCredentialsPresent = ref(false);
const showRemoveOrdersModal = ref(false);
const selectedStop: Ref<ShipmentStop | undefined> = ref(undefined);
const shipmentServiceIncidents: Ref<ShipmentServiceIncident[]> = ref([]);
const shipmentCargoInsuranceCertificate: Ref<
  Loadable<CargoInsuranceCertificate>
> = ref({ loading: false });
const relatedOrders: Ref<Loadable<Order[]>> = ref({ loading: false });
const tenderRequest: Ref<Loadable<TenderRequest>> = ref({ loading: false });
const tenderRequests: Ref<TenderRequest[]> = ref([]);
const waterfallTender: Ref<Loadable<WaterfallTender>> = ref({ loading: false });
const numDirtyRefNums = ref(0);
const numDirtyAccessorials = ref(0);
const numDirtyLineItems = ref(0);
const numDirtyStops = ref(0);
const internalTags = ref([...shipment.value.tags]);
const tagCancelToken: Ref<CancelTokenSource | undefined> = ref();
const brokerageInfo: Ref<BrokerageInfo> = ref(newBrokerageQuoteData());
const carrierSettings: Ref<CarrierSetting> = ref(new CarrierSetting());
const assignableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });
const manifest: Ref<Loadable<Manifest>> = ref({ loading: false });

const cancelShipmentModal = ref({
  isVisible: false,
  loading: false,
  carrierNotificationErrorMessage: undefined,
});
const requiresManualCancellation = computed(
  () =>
    tenderRequest.value.data?.tenderResponse?.responseMethod ===
    TenderMethod.MANUAL,
);
const emailDocumentsModalVisible = ref(false);

const resendConfirmationModal = ref({
  isVisible: false,
  emails: '',
});

const printShippingLabelTemplateModal = ref({
  isVisible: false,
  label: '',
});
const documentRequestResultModal: Ref<DocumentRequestResultModalData> = ref({
  isVisible: false,
  response: [],
});
const sharingModal = ref({
  isVisible: false,
  isLoading: false,
  accessToken: undefined as string | undefined,
});
const serviceIncidentTypes: Ref<Loadable<ServiceIncidentType[]>> = ref({
  loading: false,
});
const macroPointAdditionalInfoModal = ref({
  isVisible: false,
  shipmentId: shipmentId.value,
  requiredReferenceNumbers: [] as string[],
});
const purchaseCargoInsuranceModal = ref({
  isVisible: false,
});
const cargoInsuranceCancellationModal = ref({ isVisible: false });
const sendCheckCallRequestsModal = ref({
  isVisible: false,
  emails: '',
});
const inviteCarriersToSpotMarketModal = ref({
  visible: false,
  sending: false,
  inviteCommand: new ShipmentInviteCarriersToSpotMarketCommand({
    shipmentId: shipmentId.value,
  }),
});
const spotMarketErrorModal = ref({
  visible: false,
  erroredSpotMarkets: [] as SpotMarket[],
});

const lineItemRemovedModal = ref({
  visible: false,
  isShipOrderSplit: false,
  removedLineItem: new ShipmentLineItem(),
});
const rateSelectAndTenderModal = ref({
  rateQuote: new RateQuote(),
  tenderMethod: TenderMethod.MANUAL,
  tenderOnly: false,
  visible: false,
  rateSelectionReason: undefined as RateSelectionReason | undefined,
});
const ordersAndAccessTokens = ref(
  [] as { id: number; accessToken: string; suggestedEmails: string[] }[],
);

const waterfallTenderModalVisible = ref(false);

const shipmentStops = toRef(() => shipment.value.stops);

const lowestCostReasonModal = ref({
  visible: false,
  selectedQuote: new RateQuote(),
  isBrokerage: false,
});

const showBolOverrideModal = computed(
  () => isJasperBolEnabled.value && isBolHandlingUnitOverrideEnabled.value,
);

const canDeleteTags = computed((): boolean =>
  authorization.hasAuthority('tag', 'manage'),
);

const shipmentEditingLocked = computed(() => {
  return isStatusEqualOrAfter(shipment.value.status, ShipmentStatus.COMPLETE);
});

const searchParams = computed(() => {
  return router.currentRoute.value.query.search as string;
});

const userCanEditShipment = computed(() => {
  const hasEditShipmentPermission = authorization.hasAuthority(
    'shipment',
    'update',
  );
  if (!hasEditShipmentPermission || shipmentEditingLocked.value) {
    return false;
  }
  if (
    isStatusEqualOrBefore(
      shipment.value.status,
      ShipmentStatus.CARRIER_ASSIGNED,
    )
  ) {
    return true;
  }
  if (
    isStatusEqualOrBefore(shipment.value.status, ShipmentStatus.BOOKED) &&
    authorization.hasAuthority('shipment', 'updateBookedShipmentLocations')
  ) {
    return true;
  }
  if (authorization.hasAuthority('shipment', 'updateLockedFields')) {
    return true;
  }
  return false;
});

const isAnyDirty = computed(() => {
  return (
    v$.value.$anyDirty ||
    !!numDirtyLineItems.value ||
    !!numDirtyRefNums.value ||
    !!numDirtyAccessorials.value ||
    !!numDirtyStops.value
  );
});

const brokerageDates = computed(
  (): BrokerageDates =>
    new BrokerageDates({
      pickupTimeZone: shipment.value.origin?.location?.timeZone,
      pickupEarliest:
        shipment.value.origin?.scheduledAppointmentEarliest ??
        shipment.value.origin?.requestedAppointmentEarliest,
      pickupLatest:
        shipment.value.origin?.scheduledAppointmentLatest ??
        shipment.value.origin?.requestedAppointmentLatest,
      deliveryTimeZone: shipment.value.destination?.location?.timeZone,
      deliveryEarliest:
        shipment.value.destination?.scheduledAppointmentEarliest ??
        shipment.value.destination?.requestedAppointmentEarliest,
      deliveryLatest:
        shipment.value.destination?.scheduledAppointmentLatest ??
        shipment.value.destination?.requestedAppointmentLatest,
    }),
);

const statusTooltip = computed(() => {
  if (shipment.value.swc) {
    return t('swc.shipWithoutContact');
  } else if (shipment.value.status) {
    return translateEnum(shipment.value.status, 'ShipmentStatus');
  } else {
    return '-';
  }
});

const orderLevelReferenceNumbers = computed(() => {
  return shipment.value.referenceNumbers.filter(
    (x) => x.source?.type === ReferenceNumberTypeLevel.ORDER,
  );
});

const shipmentLevelReferenceNumbers = computed({
  get: () => {
    return shipment.value.referenceNumbers.filter(
      (x) => x.source?.type === ReferenceNumberTypeLevel.SHIPMENT,
    );
  },
  set: (newVal: ReferenceNumber[]) => {
    // We only pass shipment level refNums down to `ReferenceNumberSection`
    // When it updates, concat newVal with the order level refNums to rebuild
    // the complete list for the shipment
    shipment.value.referenceNumbers = newVal.concat(
      orderLevelReferenceNumbers.value,
    );
  },
});

const baseUrl = computed(() => `${window.location.origin}/corsair`);

const relatedOrdersSharingUrls = computed(() => {
  return (
    ordersAndAccessTokens.value?.map((order) => ({
      id: order.id,
      url: `${baseUrl.value}/order/${order.id}/tracking?accessToken=${order.accessToken}`,
      suggestedEmails: order.suggestedEmails,
    })) ?? []
  );
});

const shippingLabelDocuments = computed(
  (): SignedDocument[] =>
    documents.value.data?.filter((doc) =>
      doc.types.includes(CorsairDocumentType.SHIPPING_LABEL),
    ) ?? [],
);

const getLatestShippingLabelDocumentByCreatedDate = computed(
  (): SignedDocument | undefined =>
    sortShippingLabelsNewestFirst(shippingLabelDocuments.value)[0],
);

const stopLevelAccessorials = computed({
  get(): { sequenceNumber: number; accessorials: AccessorialCode[] }[] {
    return shipment.value.stops.map((s) => ({
      sequenceNumber: s.sequenceNumber ?? -1,
      accessorials: s.requestedAccessorials ?? [],
    }));
  },
  set(newVal: { sequenceNumber: number; accessorials: AccessorialCode[] }[]) {
    newVal.forEach((accessorialsByStop) => {
      const stop = shipment.value.stops.find(
        (s) => s.sequenceNumber === accessorialsByStop.sequenceNumber,
      );
      if (stop) {
        stop.requestedAccessorials = accessorialsByStop.accessorials;
      }
    });
  },
});

const loadServiceIncidentTypes = async () => {
  if (!authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    serviceIncidentTypes.value.loading = true;
    serviceIncidentTypes.value.data = await api.serviceIncidentType.findAll();
  } catch (e) {
    console.error('Error loading incident types', e);
    serviceIncidentTypes.value.error = e;
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.type', 2),
      }),
      error: e,
    });
  } finally {
    serviceIncidentTypes.value.loading = false;
  }
};

const loadAssignableUsers = async () => {
  const ownerId = shipment.value.owner?.id;
  if (!ownerId) {
    console.error('Cannot determine owner id for shipment', shipment.value);
    return;
  }

  assignableUsers.value = loadAssignableUsersByDomainId(ownerId, 'shipment');
};

const saveAssignedUsers = async (newVal: UserRef[]) => {
  if (!shipment.value.id) {
    return;
  }

  try {
    assignableUsers.value.loading = true;
    await api.shipment.updateAssignedUsers(shipment.value.id, newVal);
    shipment.value.assignedUsers = newVal;
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('users.users', 2),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving assigned users for shipment', e);
    toast.addError({
      error: e,
      detail: t('errors.savingParam', {
        param: t('common.assignParam', { param: t('users.users', 2) }),
      }),
    });
  } finally {
    assignableUsers.value.loading = false;
  }
};

const getSharingModalData = async () => {
  ordersAndAccessTokens.value = [];
  if (relatedOrders.value.data === undefined) {
    return;
  }

  let defaultOrderData: DefaultOrderData | undefined;

  if (authorization.hasAuthority('client', 'read')) {
    try {
      defaultOrderData = await api.client.getDefaultOrderData();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error getting default data.', e);
      toast.addError({
        detail: t('errors.defaultData'),
        error: e,
      });
    }
  }

  relatedOrders.value.data.forEach(async (order: Order) => {
    const accessToken = await getAccessToken(order.id);
    const suggestedEmails = [];

    if (
      defaultOrderData?.sharingEmailTypes?.includes(SharingEmailType.ORIGIN) &&
      !!order.origin?.contact.contactEmail
    ) {
      suggestedEmails.push(order.origin.contact.contactEmail);
    }

    if (
      defaultOrderData?.sharingEmailTypes?.includes(
        SharingEmailType.DESTINATION,
      ) &&
      !!order.destination?.contact.contactEmail
    ) {
      suggestedEmails.push(order.destination.contact.contactEmail);
    }

    if (order.id) {
      ordersAndAccessTokens.value.push({
        id: order.id,
        accessToken,
        suggestedEmails,
      });
    }
  });
};

const loadShipment = async (shipmentId: number): Promise<void> => {
  try {
    loading.value = true;
    shipment.value = await api.shipment.findById(shipmentId);
    setParentEntity(shipment);

    // do everything else on the next tick so `parentEntity`s dependencies
    // have time to update
    nextTick(async () => {
      // All of these load fields on the shipment so we need to wait for them to
      // finish before calling `reset`.
      await Promise.all([
        loadShipmentEvents(),
        loadShipmentServiceIncidents(),
        loadServiceIncidentTypes(),
        loadTenderRequests(),
        loadShipmentCargoInsuranceCertificate(),
        loadRateShop(),
        loadWaterfallTender(),
        reloadShipmentFinancials(),
      ]);
      reset(shipment.value);

      loadRelatedOrders();
      getCanViewUpliftPermission(shipmentId);
      loadBolTemplateOptions(shipmentId);
      loadCommercialIntegrationBooleans();
      loadTags();
      loadManifest();
      if (shipment.value.acceptedRateQuoteId) {
        performTenderAuthorization(shipment.value.acceptedRateQuoteId);
      }
      loadCheckCallStatus(shipmentId);
    });
  } catch (e) {
    if (axios.isCancel(e) || (await handleRequiredDomainRedirect(e))) {
      return;
    }

    console.error('Error loading Shipment', shipmentId, e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('shipments.shipments'),
        id: shipmentId,
      }),
      error: e,
    });
    throw e;
  } finally {
    loading.value = false;
  }
};

const getCanViewUpliftPermission = async (shipmentId: number) => {
  try {
    canViewUplift.value =
      await api.user.getHasUpliftShipmentPermission(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error fetching uplift permission for shipment ${shipmentId}`,
      e,
    );
    canViewUplift.value = false;
  }
};

const loadBolTemplateOptions = async (shipmentId: number) => {
  try {
    bolTemplateOptions.value.loading = true;
    bolTemplateOptions.value.data =
      await api.documentTemplate.matchDocumentTemplate(
        DocumentType.BOL,
        shipmentId,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    console.error('Error loading BOL templates for shipment', shipmentId, e);
    bolTemplateOptions.value.data = undefined;
  } finally {
    bolTemplateOptions.value.loading = false;
  }
};

const onScrollToTimeline = (incidentId: number) => {
  shipmentEventSection.value?.toggleTimeline(true);

  nextTick(() => {
    const serviceIncidentElement = document.querySelector(
      `#shipment-service-incident-${incidentId}`,
    ) as HTMLElement;
    scrollIntoView(serviceIncidentElement);
  });
};

const loadCommercialIntegrationBooleans = async () => {
  try {
    macroPointCredentialsPresent.value = await api.client.isMacroPointEnabled();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching MacroPoint credentials', e);
    macroPointCredentialsPresent.value = false;
  }
};

const loadCarrierSettings = async () => {
  const scac = shipment.value.scac;
  if (!scac || !authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    carrierSettings.value =
      shipment.value.carrierType === CapacityProviderType.CARRIER
        ? await api.carrier.findCarrierSettingsForScac(scac)
        : await api.broker.findBrokerSettingsForScac(scac);

    carrierDirectCredentialsPresent.value =
      await api.carrier.areCarrierDirectCredentialsConfiguredForScac(scac);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading carrier settings', e);
  }
};

const loadShipmentEvents = async () => {
  if (
    !authorization.hasAuthority('shipmentEvent', 'read') ||
    !shipment.value.id
  ) {
    return;
  }

  try {
    shipmentEvents.value = await api.shipmentEvent.findAllByShipment(
      shipment.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching shipment events', e);
  }
};

const loadShipmentServiceIncidents = async () => {
  if (
    !authorization.hasAuthority('shipmentEvent', 'read') ||
    !shipment.value.id
  ) {
    return;
  }

  try {
    shipmentServiceIncidents.value =
      await api.shipmentServiceIncident.findAllByShipment(shipment.value.id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching shipment service incidents', e);
  }
};

const loadRelatedOrders = async () => {
  if (!authorization.hasAuthority('order', 'read') || !shipment.value.id) {
    return;
  }

  try {
    relatedOrders.value.loading = true;
    relatedOrders.value.data = await api.shipment.findRelatedOrders(
      shipment.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    relatedOrders.value.error = e as Error;
    console.error('Error loading related orders', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.relatedOrders'),
      }),
      error: e,
    });
  } finally {
    relatedOrders.value.loading = false;
  }
};

const loadWaterfallTender = async () => {
  if (!shipment.value.id || !isWaterfallTenderingEnabled.value) {
    return;
  }

  try {
    waterfallTender.value.loading = true;
    waterfallTender.value.data = await api.shipment.findActiveWaterfallTender(
      shipment.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    waterfallTender.value.error = e as Error;
    console.error('Error loading related waterfall tender', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
      error: e,
    });
  } finally {
    waterfallTender.value.loading = false;
  }
};

const loadTenderRequests = async () => {
  if (!shipment.value.id) {
    return;
  }

  // Clear any data present on tenderRequest in case user changed carriers
  tenderRequest.value = { loading: true };

  try {
    tenderRequests.value = await api.shipment.getAllTenders(shipment.value.id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching all tenders', e);
  }

  try {
    tenderRequest.value.data = await api.shipment.getActiveTender(
      shipment.value.id,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // a shipment not having an active tender is an expected state
      return;
    }
    tenderRequest.value.error = e as Error;
    console.error('Error loading tender request', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('Tender'),
      }),
      error: e,
    });
  } finally {
    tenderRequest.value.loading = false;
  }
};

const loadShipmentCargoInsuranceCertificate = async () => {
  if (!shipment.value.id) {
    return;
  }

  shipmentCargoInsuranceCertificate.value.data = undefined;
  shipmentCargoInsuranceCertificate.value.loading = true;
  try {
    shipmentCargoInsuranceCertificate.value.data =
      await api.shipmentCargoInsurance.getCertificateForShipment(
        shipment.value.id,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // a shipment not having a cargo insurance certificate is an expected state
      return;
    }
    shipmentCargoInsuranceCertificate.value.error = e as Error;
    console.error('Error cargo insurance certificate', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('Cargo Insurance Certificate'),
      }),
      error: e,
    });
  } finally {
    shipmentCargoInsuranceCertificate.value.loading = false;
  }
};

const loadManifest = async () => {
  if (!shipment.value.id) {
    return;
  }

  manifest.value.data = undefined;
  manifest.value.loading = true;
  try {
    manifest.value.data = await api.shipment.getActiveManifest(
      shipment.value.id,
    );
  } catch (e) {
    manifest.value.error = e as Error;
    console.error('Error loading manifest', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('manifests.manifests'),
      }),
      error: e,
    });
  } finally {
    manifest.value.loading = false;
  }
};

const onUpdateRates = () => {
  updateRates(() => {
    loadWaterfallTender();
  });
};

const onRateShopCompleted = async () => {
  loading.value = true;

  try {
    if (!rateShop.value.data?.id) {
      throw new Error(
        '`rate-shop-completed` emitted but rate shop does not have an id',
      );
    }

    loadSpotMarkets(rateShop.value.data.id, {
      shipmentId: shipmentId.value,
      bypassCache: true,
    });

    const newShipment = await api.shipment.findById(shipmentId.value);

    // We want to only reload the shipment for certain actions taken (auto-tender)
    // otherwise user will lose unsaved changes. Ideally the rate shop would
    // return enough information to determine whether we should reload or not,
    // but for now only reload whenever status changes
    if (shipment.value.status === newShipment.status) {
      return;
    }

    shipment.value = newShipment;
    setParentEntity(shipment);

    // do everything else on the next tick so `parentEntity`s dependencies
    // have time to update
    nextTick(async () => {
      // All of these load fields on the shipment so we need to wait for them to
      // finish before calling `reset`.
      await Promise.all([
        loadShipmentEvents(),
        loadTenderRequests(),
        loadWaterfallTender(),
        reloadShipmentFinancials(),
        loadCommercialIntegrationBooleans(),
      ]);
      reset(shipment.value);
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error refreshing data after rate shop completed', e);
  } finally {
    loading.value = false;
  }
};

const onRateQuoteSelected = (payload: AcceptQuotePayload) => {
  showRateAssignAndTenderModal(payload.rateQuote, payload.rateSelectionReason);
};

const onRateQuoteAssigned = (rateQuote: RateQuote) => {
  const rateSelectionReason = rateSelectAndTenderModal.value.rateSelectionReason
    ? new RateSelectionReason({
        ...rateSelectAndTenderModal.value.rateSelectionReason,
        shipmentId: shipment.value.id,
        rateQuoteId: rateQuote.id,
      })
    : undefined;

  acceptRateQuote(rateQuote, rateSelectionReason, () => {
    loadShipment(shipmentId.value);
  });
};

const showRateAssignAndTenderModal = async (
  rateQuote: RateQuote,
  rateSelectionReason?: RateSelectionReason,
) => {
  rateSelectAndTenderModal.value.rateQuote = new RateQuote(rateQuote);
  rateSelectAndTenderModal.value.rateSelectionReason = rateSelectionReason;
  // If selecting a brokerage quote from the rate shop, it should be treated the same as a carrier quote
  // If using the "send to brokerage" option from the rate shop action button the quote will not have an id and should be treated differently
  rateSelectAndTenderModal.value.tenderOnly =
    rateQuote.source === 'BROKERAGE' && !rateQuote?.id;
  rateSelectAndTenderModal.value.visible = true;
};

const onSpotQuoteAdded = async (spotQuote: RateQuote) => {
  addSpotQuote(spotQuote, () => {
    loadRateShop();
    reloadShipmentFinancials();
  });
};

const onBrokerageAdded = async (brokeragePayload: BrokeragePayload) => {
  loading.value = true;
  try {
    await addBrokerageQuote(brokeragePayload, () => {
      loadShipment(shipmentId.value);
    });
  } catch (e) {
    if (isBadRequest(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      brokerageInfo.value.isShowingModal = true;
      brokerageInfo.value.error = errorResponse;
    }
  } finally {
    loading.value = false;
  }
};

const reloadSpotMarkets = async (rateQuote: RateQuote) => {
  const rateShopId = rateShop.value.data?.id;
  if (!rateShopId || !shipmentId.value) {
    return;
  }

  if (rateQuote.source === 'SPOT_MARKET') {
    loadSpotMarkets(rateShopId, {
      shipmentId: shipmentId.value,
      bypassCache: true,
    });
  }
};

const refreshShipment = async () => {
  shipment.value = await api.shipment.findById(shipmentId.value);
  reset(shipment.value);
};

const onQuoteUpdated = (rateQuote: RateQuote) => {
  updateRateQuote(rateQuote, () => {
    loadRateShop();
    loadWaterfallTender();
    reloadShipmentFinancials();
    refreshShipment();
    reloadSpotMarkets(rateQuote);
  });
};

const onSpotQuoteDeleted = (spotQuote: RateQuote) => {
  deleteSpotQuote(spotQuote, () => {
    loadRateShop();
    reloadShipmentFinancials();
  });
};

const checkCallEmailModalShow = () => {
  const emailTenderConfig = carrierSettings.value.tenderConfig.find(
    (x: TenderConfig) => x.method === TenderMethod.EMAIL,
  );
  sendCheckCallRequestsModal.value.emails =
    emailTenderConfig && emailTenderConfig?.configOptions
      ? emailTenderConfig.configOptions?.emails
      : '';
  sendCheckCallRequestsModal.value.isVisible = true;
};

const tenderModalShow = (tenderMethod?: TenderMethod) => {
  if (tenderMethod) {
    rateSelectAndTenderModal.value.tenderMethod = tenderMethod;
  }

  if (acceptedRateQuote.value) {
    rateSelectAndTenderModal.value.rateQuote = acceptedRateQuote.value;
    rateSelectAndTenderModal.value.tenderOnly = true;
    rateSelectAndTenderModal.value.visible = true;
  }
};

const onSendToBrokerage = (payload: AcceptQuotePayload) => {
  brokerageInfo.value = newBrokerageQuoteData(
    payload.rateQuote,
    payload.rateSelectionReason,
  );
  brokerageInfo.value.isShowingModal = true;
};

const onBrokerageEdit = () => {
  brokerageInfo.value.data = new RateQuote(acceptedRateQuote.value);
  brokerageInfo.value.isShowingModal = true;
};

const onBrokerageConfirmed = async (brokeragePayload: BrokeragePayload) => {
  const acceptedRateQuoteId = acceptedRateQuote.value?.id;
  const brokerageRateQuoteId = brokeragePayload.brokerageQuote.id;
  brokerageInfo.value.dates = brokeragePayload.brokerageDates;
  const modifyingAcceptedBrokerageQuote =
    !!acceptedRateQuoteId && acceptedRateQuoteId === brokerageRateQuoteId;
  if (modifyingAcceptedBrokerageQuote) {
    updateBrokerageQuote(brokeragePayload, () =>
      loadShipment(shipmentId.value),
    );
  } else {
    if (!brokerageRateQuoteId && isRateSelectionReasonRequired.value) {
      // when brokeragePayload.brokerageQuote.id is null this is the "send to brokerage" flow
      // we ALWAYS want to show the modal
      // if the user is selecting a brokerage that came through the rate shop,
      // the modal would have been shown the normal way
      lowestCostReasonModal.value.selectedQuote =
        brokeragePayload.brokerageQuote;
      lowestCostReasonModal.value.isBrokerage = true;
      lowestCostReasonModal.value.visible = true;
    } else {
      await showRateAssignAndTenderModal(
        brokeragePayload.brokerageQuote,
        brokerageInfo.value.rateSelectionReason,
      );
    }
  }
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);

  // NOW show the tender modal with the rate selection reason
  showRateAssignAndTenderModal(
    lowestCostReasonModal.value.selectedQuote,
    rateSelectionReason,
  );

  // Reset modal state
  lowestCostReasonModal.value.visible = false;
  lowestCostReasonModal.value.isBrokerage = false;
};

const saveShipment = async (): Promise<boolean> => {
  if (!(await validate())) {
    return false;
  }

  const stopsChanged = numDirtyStops.value > 0;

  try {
    saving.value = true;
    shipment.value = await api.shipment.update(shipment.value);
    loadShipmentEvents();
    loadShipmentServiceIncidents();
    loadRateShop();
    loadWaterfallTender();
    reloadShipmentFinancials();
    reset(shipment.value);
    if (stopsChanged) {
      refreshRouteGeoJson();
      refreshStaticMapUrl();
    }
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('shipments.shipments'),
      }),
    });
    return true;
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    console.error('Error saving shipment', e);
    if (isBadRequest(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      setServerErrors(errorResponse);

      getFlattenedFieldErrors(errorResponse).forEach((error) => {
        toast.addError({
          detail: error,
          error: e,
        });
      });
    } else {
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('shipments.shipments'),
        }),
        error: e,
      });
    }
    return false;
  } finally {
    saving.value = false;
  }
};

const reloadShipmentFinancials = () => {
  const s =
    shipmentFinancialsSection.value as unknown as typeof ShipmentFinancialsSection;
  s?.loadShipmentFinancials(1000);
};

const onAddShipmentEvent = () => {
  showShipmentEventModal(new ShipmentEvent());
};

const showShipmentEventModal = (shimentEvent: ShipmentEvent) => {
  shipmentEventSection.value?.showEventModal(shimentEvent);
};

const showShipmentIncidentModal = (incident: ShipmentServiceIncident) => {
  shipmentEventSection.value?.showIncidentModal(incident);
};

const reset = (shipment: Shipment) => {
  resetModel(shipment);
  numDirtyLineItems.value = 0;
  numDirtyRefNums.value = 0;
  numDirtyAccessorials.value = 0;
  numDirtyStops.value = 0;
  const l = lineItemSection.value as unknown as typeof LineItemSection;
  l?.reset();
  const s = locationSection.value as unknown as typeof LocationSection;
  s?.reset();
  const a = accessorialSection.value as unknown as typeof AccessorialSection;
  a?.reset();
};

const onShipmentEventOrServiceIncidentSaved = () => {
  loadShipment(shipmentId.value);
  refreshRouteGeoJson();
  refreshStaticMapUrl();
};

const onShipmentActionCommand = ({ action }: ActionCommand<ShipmentAction>) => {
  if (action === ShipmentAction.CANCEL) {
    cancelShipment();
  } else if (action === ShipmentAction.CANCEL_WATERFALL_TENDER) {
    onCancelWaterfallTender();
  } else if (action === ShipmentAction.CANCEL_CHECK_CALL_EMAILS) {
    cancelCheckCallEmails(shipmentId.value);
  } else if (action === ShipmentAction.CARRIER_UNASSIGN) {
    if (shipment.value.status === ShipmentStatus.BOOKED) {
      confirm.require({
        group: 'rygen-confirm-dialog',
        header: t('common.unassignParam', {
          param: t('carriers.carriers'),
        }),
        message: t('shipments.removeCarriersMessage'),
        accept: carrierUnassign,
        acceptClass: 'p-button-sm',
        acceptIcon: 'pi pi-check',
        acceptLabel: t('common.unassignParam', {
          param: t('carriers.carriers'),
        }),
        rejectClass: 'p-button-sm p-button-text',
        rejectLabel: t('common.cancel'),
      });
    } else {
      carrierUnassign();
    }
  } else if (action === ShipmentAction.CONSOLIDATE) {
    consolidate();
  } else if (action === ShipmentAction.EMAIL_DOCUMENTS) {
    emailDocumentsModalShow();
  } else if (action === ShipmentAction.EVENT_DELIVERED_CREATE) {
    eventDeliveredCreate();
  } else if (action === ShipmentAction.EVENT_DELIVERED_UNDO) {
    eventDeliveredUndo();
  } else if (action === ShipmentAction.EVENT_MISC_CREATE) {
    eventMiscCreate();
  } else if (action === ShipmentAction.EVENT_PICKUP_CREATE) {
    eventPickupCreate();
  } else if (action === ShipmentAction.EVENT_PICKUP_UNDO) {
    eventPickupUndo();
  } else if (action === ShipmentAction.EVENT_SERVICE_FAILURE_CREATE) {
    eventServiceIncidentCreate();
  } else if (action === ShipmentAction.INITIATE_MACROPOINT_TRACKING) {
    initiateMacroPointTracking();
  } else if (action === ShipmentAction.PRINT_BOL) {
    printBolSelection();
  } else if (action === ShipmentAction.PRINT_SHIPPING_LABEL_MODAL) {
    printShippingLabel();
  } else if (action === ShipmentAction.CARGO_INSURANCE_PURCHASE) {
    cargoInsurancePurchase();
  } else if (action === ShipmentAction.CARGO_INSURANCE_CANCEL) {
    cargoInsuranceCancel();
  } else if (action === ShipmentAction.REMOVE_ORDERS) {
    startRemoveOrdersAction();
  } else if (action === ShipmentAction.REQUEST_DOCUMENTS) {
    requestDocuments();
  } else if (action === ShipmentAction.REQUEST_SPOT_MARKET_BID) {
    confirmShowInviteCarriersToSpotMarket();
  } else if (action === ShipmentAction.REQUEST_TRACKING) {
    const rateStrategyType = acceptedRateQuote?.value?.rateStrategyType ?? '';
    if (
      P44_RATE_STRATEGIES.includes(rateStrategyType as WellKnownRateStrategy)
    ) {
      requestP44Tracking();
    } else {
      requestApiTracking();
    }
  } else if (action === ShipmentAction.RESEND_CONFIRMATION) {
    resendConfirmationModalShow();
  } else if (action === ShipmentAction.SEND_CHECK_CALL_REQUEST) {
    checkCallEmailModalShow();
  } else if (action === ShipmentAction.SHARE) {
    openSharingModal();
  } else if (action === ShipmentAction.TENDER_CONFIRM) {
    tenderConfirm();
  } else if (action === ShipmentAction.TENDER_TO_CARRIER) {
    tenderModalShow();
  } else if (action === ShipmentAction.TENDER_REJECT) {
    tenderReject();
  } else if (action === ShipmentAction.TENDER_RESEND) {
    tenderResend();
  } else if (action === ShipmentAction.WATERFALL_TENDER) {
    waterfallTenderModalShow();
  } else {
    console.warn('Shipment action not implemented:', action);
  }
};

const requestP44Tracking = async () => {
  try {
    const trackingResponse = await api.shipment.requestP44Tracking(
      shipmentId.value,
    );
    if (trackingResponse.accepted) {
      toast.addSuccess({
        detail: t('shipments.trackingRequestSuccess'),
      });
    } else {
      toast.addError({
        detail:
          t('shipments.errors.sendingTrackingRequest') +
          '\n\n' +
          trackingResponse.infoMessages,
        error: trackingResponse.infoMessages,
      });
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('shipments.errors.sendingTrackingRequest'),
      error: e,
    });
  }
};

const requestApiTracking = async () => {
  try {
    await api.shipment.requestApiTracking(shipmentId.value);
    toast.addSuccess({
      detail: t('shipments.trackingApiRequestComplete'),
    });
    loadShipment(shipmentId.value);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('shipments.errors.sendingApiTrackingRequest'),
      error: e,
    });
  }
};

const requestDocuments = async () => {
  try {
    const result = await api.shipment.requestDocuments(shipmentId.value);
    documentRequestResultModal.value.response = result;
    documentRequestResultModal.value.isVisible = true;
    loadShipment(shipmentId.value);
    toast.addSuccess({
      detail: t('shipments.documentRequestSuccess'),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('shipments.errors.sendingDocumentRequest'),
      error: e,
    });
  }
};

const showInviteCarriersToSpotMarketModal = () => {
  inviteCarriersToSpotMarketModal.value.visible = true;
  inviteCarriersToSpotMarketModal.value.inviteCommand.shipmentId =
    shipmentId.value;
  inviteCarriersToSpotMarketModal.value.inviteCommand.equipmentType =
    shipment.value.equipmentType;
};

const confirmShowInviteCarriersToSpotMarket = () => {
  if (isAnyDirty.value) {
    confirm.require({
      group: 'rygen-confirm-dialog',
      message: t('carrierPortal.spotMarket.unsavedChangesWarning'),
      header: t('dirtyTracking.unsavedChanges'),
      icon: 'pi pi-exclamation-triangle text-warning',
      acceptClass: 'p-button-sm p-button-warn',
      rejectClass: 'p-button-sm p-button-text',
      acceptLabel: t('dirtyTracking.saveAndContinue'),
      rejectLabel: t('common.cancel'),
      accept: async () => {
        // When confirm dialog closes, PrimeVue will try to remove the
        // `p-overflow-hidden` class that blocks scrolling. It will keep trying
        // to remove this during saving so we add our own class too.
        // https://github.com/primefaces/primevue/issues/3953
        // https://github.com/primefaces/primevue/issues/3981
        document.body.classList.add('p-overflow-hidden');
        document.body.classList.add('p-overflow-hidden-force');

        await saveShipment();
        showInviteCarriersToSpotMarketModal();

        // Wait for PrimeVue to stop trying to remove the class and add it back
        setTimeout(() => {
          document.body.classList.remove('p-overflow-hidden-force');
          document.body.classList.add('p-overflow-hidden');
        }, 300);
      },
    });
  } else {
    showInviteCarriersToSpotMarketModal();
  }
};

const updateCarrierNotes = async (carrierNotes: string) => {
  try {
    const updatedShipment = await api.shipment.updateCarrierNotes(
      shipmentId.value,
      carrierNotes,
    );

    shipment.value.carrierNotes = updatedShipment.carrierNotes;
    resetModel(shipment.value);

    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('orders.paramNotes', { param: t('carriers.carriers') }),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving carrier notes for order', e);
    toast.addError({
      error: e,
      detail: t('errors.savingParam', {
        param: t('orders.paramNotes', { param: t('carriers.carriers') }),
      }),
    });

    // Re-throw error so that we don't send spot market invite
    throw e;
  }
};

const onInviteCarriersToSpotMarket = async (
  command: ShipmentInviteCarriersToSpotMarketCommand,
  carrierNotes: string | undefined,
) => {
  if (command.shipmentId == null) {
    throw new Error(
      '`shipmentId` is required to invite carriers to spot market',
    );
  }

  try {
    inviteCarriersToSpotMarketModal.value.sending = true;

    if (carrierNotes !== undefined) {
      await updateCarrierNotes(carrierNotes);
    }

    const result = await inviteCarriersToSpotMarket(command);
    inviteCarriersToSpotMarketModal.value.visible = false;

    if (result.validSpotMarkets.length) {
      shipmentRateShop.value?.showSpotMarketTab();
    }

    startSpotMarketPollingForResult(result, {
      shipmentId: command.shipmentId,
    });

    if (result.erroredSpotMarkets.length) {
      spotMarketErrorModal.value.erroredSpotMarkets = result.erroredSpotMarkets;
      spotMarketErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // generic error handling happened within inviteCarriersToSpotMarket and
    // updateCarrierNotes. no specific handling needed
  } finally {
    inviteCarriersToSpotMarketModal.value.sending = false;
  }
};

const initiateMacroPointTracking = async () => {
  loading.value = true;
  try {
    const response =
      await api.shipment.checkRequirementsAndStartMacroPointTracking(
        shipmentId.value,
      );
    if (response.status === 'STARTED') {
      toast.addSuccess({
        detail: t('shipments.macroPoint.macroPointTrackingStarted'),
      });
      loadShipment(shipmentId.value);
    } else if (response.status === 'NEEDS_INFO') {
      macroPointAdditionalInfoModal.value.requiredReferenceNumbers =
        response.requiredReferenceNumbers ?? [];
      macroPointAdditionalInfoModal.value.isVisible = true;
    } else if (response.status === 'ERROR') {
      throw new Error(response.error);
    } else {
      throw new Error(`Unexpected tracking status: ${response.status}`);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    let errorMessage = '';
    if (axios.isAxiosError(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      errorMessage = errorResponse.exceptionMessage;
    } else {
      const error = e as Error;
      errorMessage = error.message;
    }
    toast.addError({
      detail:
        t('shipments.macroPoint.errors.macroPointTracking') +
        ':\n' +
        errorMessage,
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

const addAdditionalInfoAndInitiateMacroPointTracking = async (
  additionalInfo: ReferenceNumber[],
) => {
  const merged = [...shipmentLevelReferenceNumbers.value];
  for (const incoming of additionalInfo) {
    const existing = merged.find(
      (r) => r.typeNaturalId === incoming.typeNaturalId,
    );
    if (existing) {
      existing.value = incoming.value;
    } else {
      merged.push(incoming);
    }
  }
  shipmentLevelReferenceNumbers.value = merged;
  const saved = await saveShipment();
  if (!saved) {
    return;
  }
  await initiateMacroPointTracking();
};

const onTenderToCarrier = async (
  selectedRateQuote: RateQuote,
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
  confirmTenderSentCallback: () => void,
) => {
  try {
    if (tenderMethod === TenderMethod.MCLEOD) {
      await onBrokerageAdded({
        brokerageQuote: selectedRateQuote,
        brokerageDates: brokerageInfo.value.dates,
        rateSelectionReason: rateSelectAndTenderModal.value.rateSelectionReason,
      });
      return;
    }
    if (!selectedRateQuote.id && selectedRateQuote.source === 'BROKERAGE') {
      selectedRateQuote = await api.shipment.addSpotQuote(
        shipmentId.value,
        selectedRateQuote,
      );
      sendTenderToCarrier(
        tenderMethod,
        tenderConfigOptions,
        confirmTenderSentCallback,
        selectedRateQuote,
      );
    } else {
      sendTenderToCarrier(
        tenderMethod,
        tenderConfigOptions,
        confirmTenderSentCallback,
      );
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error accepting rate quote', e);
    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;

    toast.addError({
      detail: t('rateShop.errorAcceptingRateQuoteParam', {
        param: errorDetail ? errorDetail : '',
      }),
      error: e,
    });
  }
};

const onAcceptRateQuoteAndTenderToCarrier = (
  selectedRateQuote: RateQuote,
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
  confirmTenderSentCallback: () => void,
) => {
  sendTenderToCarrier(
    tenderMethod,
    tenderConfigOptions,
    confirmTenderSentCallback,
    selectedRateQuote,
  );
};

const sendTenderToCarrier = async (
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
  confirmTenderSentCallback: () => void,
  rateQuote?: RateQuote,
) => {
  try {
    saving.value = true;
    tenderRequest.value.loading = true;

    const rateSelectionReason = rateSelectAndTenderModal.value
      .rateSelectionReason
      ? new RateSelectionReason({
          ...rateSelectAndTenderModal.value.rateSelectionReason,
          shipmentId: shipment.value.id,
          rateQuoteId: rateQuote?.id,
        })
      : undefined;

    const tenderToCarrierRequest = new TenderToCarrierRequest(
      tenderMethod,
      tenderConfigOptions,
      rateSelectionReason,
    );
    if (shipment.value.status === ShipmentStatus.TENDERED) {
      tenderRequest.value.data = await api.shipment.resendTender(
        shipmentId.value,
        tenderToCarrierRequest,
      );
    } else if (rateQuote?.id) {
      tenderRequest.value.data = await api.shipment.acceptRateQuoteAndTender(
        shipmentId.value,
        rateQuote.id,
        tenderToCarrierRequest,
      );
    } else {
      tenderRequest.value.data = await api.shipment.tenderToCarrier(
        shipmentId.value,
        tenderToCarrierRequest,
      );
    }

    await loadShipment(shipmentId.value);

    if (
      tenderRequest.value.data.tenderResponse?.responseCode ===
      TenderResponseCode.RESERVATION_DECLINED
    ) {
      toast.addWarning({
        detail: t('shipments.tenderToCarrierSuccessRejected'),
      });
    } else if (
      tenderRequest.value.data.tenderResponse?.responseCode ===
      TenderResponseCode.RESERVATION_ACCEPTED
    ) {
      toast.addSuccess({
        detail: t('shipments.tenderToCarrierSuccessAccepted'),
      });
    } else {
      toast.addSuccess({ detail: t('shipments.tenderToCarrierSuccess') });
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;
    console.error('tender error', shipmentId.value, e);
    toast.addError({
      detail:
        t('shipments.errors.tender') + (errorDetail ? ` - ${errorDetail}` : ''),
      error: e,
    });
    throw e;
  } finally {
    saving.value = false;
    tenderRequest.value.loading = false;
    confirmTenderSentCallback();
  }
};

const tenderResend = async () => {
  const previousRequestMethod =
    tenderRequest.value.data?.tenderMethod ?? TenderMethod.MANUAL;

  tenderModalShow(previousRequestMethod);
};

const tenderConfirm = async () => {
  loading.value = true;
  tenderRequest.value.loading = true;
  try {
    if (!shipment.value.scac) {
      throw new Error(
        'SCAC must be set before attempting to submit tender response',
      );
    }
    const tenderAcceptRequest = new TenderAcceptRequest(TenderMethod.MANUAL);
    tenderRequest.value.data = await api.shipment.tenderAccept(
      shipmentId.value,
      tenderAcceptRequest,
    );
    await loadShipment(shipmentId.value);
    toast.addSuccess({
      detail: t('messages.successConfirmingParam', {
        param: t('shipments.tender'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('tenderAccept error', shipmentId.value, e);
    toast.addError({
      detail: t('errors.confirmingParam', {
        param: t('shipments.tender'),
      }),
      error: e,
    });
    throw e;
  } finally {
    loading.value = false;
    tenderRequest.value.loading = false;
  }
};

const tenderReject = async () => {
  loading.value = true;
  tenderRequest.value.loading = true;
  try {
    const tenderRejectRequest = new TenderRejectRequest(TenderMethod.MANUAL);
    tenderRequest.value.data = await api.shipment.tenderReject(
      shipmentId.value,
      tenderRejectRequest,
    );
    await loadShipment(shipmentId.value);
    toast.addSuccess({
      detail: t('messages.successRejectingParam', {
        param: t('shipments.tender'),
      }),
    });
    // If waterfallTender exists poll until the next tender request is in progress
    if (waterfallTender.value.data?.isInProgress()) {
      refreshWaterfallTenderRequestUntilInProgress(10);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('tenderReject error', shipmentId.value, e);
    toast.addError({
      detail: t('errors.rejectingParam', {
        param: t('shipments.tender'),
      }),
      error: e,
    });
    throw e;
  } finally {
    loading.value = false;
    tenderRequest.value.loading = false;
  }
};

const refreshWaterfallTenderRequestUntilInProgress = async (
  attempts: number,
) => {
  await loadWaterfallTender();
  if (
    waterfallTender.value.data?.isInProgress() &&
    !tenderRequest.value.data?.isInProgress &&
    attempts > 0
  ) {
    setTimeout(async () => {
      await loadTenderRequests();
      await refreshWaterfallTenderRequestUntilInProgress(attempts - 1);
    }, 500);
  } else {
    await loadShipment(shipmentId.value);
  }
};

const eventPickupCreate = () => {
  if (!firstUnresolvedPickupMilestone.value) {
    console.error(
      'eventPickupCreate next unresolved pickup event could be found',
    );
    const e = new Error(`next unresolved pickup event could be found`);
    toast.addError({
      detail: t('shipments.errors.eventNotFound'),
      error: e,
    });
    throw e;
  }
  showShipmentEventModal(firstUnresolvedPickupMilestone.value);
};

const eventPickupUndo = async () => {
  try {
    loading.value = true;

    const stopToUndo = shipment.value.stops.find(
      (stop) => stop.id === lastResolvedPickupStop.value?.id,
    );

    if (stopToUndo) {
      stopToUndo.actualArrival = undefined;
      stopToUndo.actualDeparture = undefined;
      shipment.value = await api.shipment.update(shipment.value);
      reset(shipment.value);
      loadShipment(shipmentId.value);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error setting actual arrival to undefined', e);
    if (isBadRequest(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      setServerErrors(errorResponse);

      getFlattenedFieldErrors(errorResponse).forEach((error) => {
        toast.addError({
          detail: error,
          error: e,
        });
      });
    } else {
      toast.addError({
        detail: t('shipments.errors.notPickedUp'),
        error: e,
      });
    }
  } finally {
    loading.value = false;
  }
};

const eventDeliveredCreate = () => {
  if (!firstUnresolvedDropOffMilestone.value) {
    console.error(
      'eventDeliveredCreate next unresolved delivery event could be found',
    );
    const e = new Error(`next unresolved delivery event could be found`);
    toast.addError({
      detail: t('shipments.errors.eventNotFound'),
      error: e,
    });
    throw e;
  }
  showShipmentEventModal(firstUnresolvedDropOffMilestone.value);
};

const eventDeliveredUndo = async () => {
  try {
    loading.value = true;

    const stopToUndo = shipment.value.stops.findLast(
      (stop) => stop.id === lastResolvedDropOffStop.value?.id,
    );

    if (stopToUndo) {
      stopToUndo.actualArrival = undefined;
      stopToUndo.actualDeparture = undefined;
      shipment.value = await api.shipment.update(shipment.value);
      loadShipment(shipmentId.value);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error setting actual delivery to undefined', e);
    if (isBadRequest(e)) {
      const errorResponse = e.response?.data as ErrorResponse;
      setServerErrors(errorResponse);

      getFlattenedFieldErrors(errorResponse).forEach((error) => {
        toast.addError({
          detail: error,
          error: e,
        });
      });
    } else {
      toast.addError({
        detail: t('shipments.errors.deliveredUndo'),
        error: e,
      });
    }
  } finally {
    loading.value = false;
  }
};

const emailDocumentsModalShow = () => {
  emailDocumentsModalVisible.value = true;
};

const eventMiscCreate = () => {
  onAddShipmentEvent();
};

const eventServiceIncidentCreate = () => {
  showShipmentIncidentModal(
    new ShipmentServiceIncident({
      carrierId: shipment.value.carrierId,
      actualDate: DateTime.now(),
    }),
  );
};

const resendConfirmationModalShow = () => {
  const emailTenderConfig = carrierSettings.value?.tenderConfig?.find(
    (x: TenderConfig) => x.method === TenderMethod.EMAIL,
  );
  resendConfirmationModal.value.emails =
    emailTenderConfig?.configOptions?.emails;
  resendConfirmationModal.value.isVisible = true;
};

const waterfallTenderModalShow = () => {
  waterfallTenderModalVisible.value = true;
};

const resendConfirmation = async (email: string) => {
  try {
    await api.shipmentDocument.resendConfirmation(shipmentId.value, email);
    toast.addSuccess({
      detail: t('shipments.resendConfirmationSuccess'),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Resending confirmation error', shipmentId.value, e);
    toast.addError({
      detail: t('shipments.errors.resendConfirmation'),
      error: e,
    });
  }
};

const arrayBufferToJson = (arrayBuffer: ArrayBuffer) => {
  const decoder = new TextDecoder();
  const jsonString = decoder.decode(arrayBuffer);
  return JSON.parse(jsonString);
};

const printBolSelection = () => {
  if (bolTemplateOptions.value.loading) {
    return;
  }

  const hasTemplates = !!bolTemplateOptions.value.data?.templates.length;

  // If domain has BOL handling unit override, ignore templates enitrely
  if (showBolOverrideModal.value) {
    isBolTotalHandlingUnitOverrideModalVisible.value = true;
    return;
  }

  // When domain has no templates preset, download default BOL without modal
  if (!hasTemplates) {
    printBol();
    return;
  }

  isBolTemplateModalVisible.value = true;
};

const printBol = async (templateId?: number) => {
  try {
    isPrinting.value = true;
    const blob = await api.shipmentDocument.generateBol(
      shipmentId.value,
      templateId,
    );
    const fileName = `bol_shipment_${shipmentId.value}.pdf`;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_new';
    link.download = fileName;
    link.click();
    link.remove();
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    e.response.data = arrayBufferToJson(e.response.data);
    console.error('download bol error', shipmentId.value, e.response.data);
    toast.addError({
      detail: t('shipments.errors.bolDownload'),
      error: e,
    });
  } finally {
    isPrinting.value = false;
  }
};

const onBolTotalHandlingUnitOverride = async (
  totalHandlingUnitOverride: number,
) => {
  try {
    isPrinting.value = true;
    const blob =
      await api.shipmentDocument.generateBolWithTotalHandlingUnitOverride(
        shipmentId.value,
        totalHandlingUnitOverride,
      );
    const fileName = `bol_shipment_${shipmentId.value}.pdf`;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_new';
    link.download = fileName;
    link.click();
    link.remove();
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    e.response.data = arrayBufferToJson(e.response.data);
    console.error('download bol error', shipmentId.value, e.response.data);
    toast.addError({
      detail: t('shipments.errors.bolDownload'),
      error: e,
    });
  } finally {
    isPrinting.value = false;
  }
};

const printShippingLabel = () => {
  // With ZPL enabled the modal offers attached labels alongside templates, so it always opens.
  if (shippingLabelDocuments.value.length && !isZplShippingLabelEnabled.value) {
    openAttachedShippingLabel(
      getLatestShippingLabelDocumentByCreatedDate.value,
    );
  } else {
    printShippingLabelTemplateModal.value.isVisible = true;
  }
};

const printAttachedShippingLabel = async (
  label: SignedDocument,
  format: ShippingLabelFormat,
) => {
  if (format !== ShippingLabelFormat.ZPL) {
    openAttachedShippingLabel(label);
    return;
  }
  if (label.id == null) {
    return;
  }

  try {
    isPrinting.value = true;
    const blob = await api.shipmentDocument.convertAttachedShippingLabelToZpl(
      shipmentId.value,
      label.id,
    );
    deliverShippingLabel(blob, `shipping_label_${shipmentId.value}`, format, {
      openInNewTab: false,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('convert attached shipping label error', shipmentId.value, e);
    toast.addError({
      detail:
        shippingLabelErrorDetail(e) ??
        t('shipments.errors.shippingLabelDownload'),
      error: e,
    });
  } finally {
    isPrinting.value = false;
  }
};

const printShippingLabelsFromTemplate = async (
  count: string,
  templateId: number | undefined,
  format: ShippingLabelFormat,
) => {
  try {
    isPrinting.value = true;
    printShippingLabelTemplateModal.value.isVisible = false;
    const blob = await api.shipmentDocument.generateShippingLabel(
      shipmentId.value,
      count,
      templateId,
      format,
    );
    deliverShippingLabel(blob, `shipping_label_${shipmentId.value}`, format, {
      openInNewTab: true,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('download shipment label error', shipmentId.value, e);
    toast.addError({
      detail:
        shippingLabelErrorDetail(e) ??
        t('shipments.errors.shippingLabelDownload'),
      error: e,
    });
  } finally {
    isPrinting.value = false;
  }
};

const cargoInsurancePurchase = () => {
  purchaseCargoInsuranceModal.value.isVisible = true;
};

const cargoInsuranceCancel = () => {
  cargoInsuranceCancellationModal.value.isVisible = true;
};

const onCargoInsuranceCancelConfirm = async (
  cancellationRequest: CargoInsuranceCancellationRequest,
) => {
  const corsairShipmentCargoInsuranceId =
    shipmentCargoInsuranceCertificate.value.data
      ?.corsairShipmentCargoInsuranceId;
  if (!corsairShipmentCargoInsuranceId) {
    return;
  }

  try {
    saving.value = true;
    shipmentCargoInsuranceCertificate.value.data =
      await api.shipmentCargoInsurance.cancelCargoInsurance(
        shipmentId.value,
        corsairShipmentCargoInsuranceId,
        cancellationRequest,
      );
    toast.addSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('cargoInsurance.cargoInsurance'),
      }),
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    console.warn('error cancelling cargo insurance', error);
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data as any;
      const cargoInsuranceBadRequestResponse =
        new CargoInsuranceBadRequestResponse(errorData?.errorDetails);
      if (cargoInsuranceBadRequestResponse.allMessages.length) {
        confirm.require({
          group: 'rygen-confirm-dialog',
          header: t('errors.cancelingParam', {
            param: t('cargoInsurance.cargoInsurance'),
          }),
          message: cargoInsuranceBadRequestResponse.allMessages
            .map((x) => x.message)
            .join('\n'),
          acceptClass: 'p-button-sm',
          acceptLabel: t('common.done'),
          rejectClass: 'hidden',
        });
        return;
      }
    }
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('cargoInsurance.cargoInsurance'),
      }),
      error: error,
    });
  } finally {
    saving.value = false;
  }
};

const cancelShipment = () => {
  cancelShipmentModal.value.carrierNotificationErrorMessage = undefined;
  cancelShipmentModal.value.isVisible = true;
};

const onConfirmCancel = async (event: CancelShipmentPayload) => {
  saving.value = true;
  try {
    cancelShipmentModal.value.loading = true;
    shipment.value = await api.shipment.cancel(shipmentId.value, {
      ...event,
      shipmentId: shipmentId.value,
    });

    toast.addSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('shipments.shipments'),
      }),
    });
    cancelShipmentModal.value.isVisible = false;

    loadRateShop();
    loadWaterfallTender();
    loadRelatedOrders();
    reloadShipmentFinancials();
    reset(shipment.value);
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    if (
      e.isAxiosError &&
      e.response?.data?.exceptionCode?.endsWith(
        'FailedToSendCarrierCancelNoticeException',
      )
    ) {
      cancelShipmentModal.value.carrierNotificationErrorMessage =
        e?.response?.data?.exceptionMessage;
    } else {
      toast.addError({
        detail: t('shipments.cancelShipmentFailed'),
        error: e,
      });
    }
  } finally {
    saving.value = false;
    cancelShipmentModal.value.loading = false;
  }
};

const consolidate = () => {
  console.warn('consolidate');
  toast.addWarning({ detail: `You can't do this yet` });
};

const carrierUnassign = async () => {
  try {
    saving.value = true;
    shipment.value = await api.shipment.carrierUnassign(shipmentId.value);
    carrierSettings.value = new CarrierSetting();
    loadShipmentEvents();
    loadRateShop();
    loadRelatedOrders();
    loadTenderRequests();
    loadWaterfallTender();
    reloadShipmentFinancials();
    reset(shipment.value);
    toast.addSuccess({
      detail: t('messages.successUnassigningParam', {
        param: t('carriers.carriers'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.unassigningParam', {
        param: t('carriers.carriers'),
      }),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};

const getAccessToken = async (orderId: number | undefined) => {
  if (!orderId) {
    return '';
  }
  return await api.orderTrackingLink.getAccessToken(orderId);
};

const openSharingModal = async () => {
  if (!shipment.value.id) {
    return;
  }
  await getSharingModalData();
  sharingModal.value.isVisible = true;
};

const sendSharingEmails = async ({
  sharingRequest,
  orderId,
  confirmEmailSent,
}: {
  sharingRequest: SharingRequest;
  orderId: number;
  confirmEmailSent: () => void;
}) => {
  try {
    await api.orderTrackingLink.sendEmails(orderId, sharingRequest);
    toast.addSuccess({
      detail: t('shipments.trackingLinkEmailSuccess'),
    });
    sharingModal.value.isVisible = false;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('shipments.errors.sharingEmail'),
      error: e,
    });
  } finally {
    confirmEmailSent();
  }
};

const buildTagSearchRoute = (tag: string) => {
  return buildSearchRoute('shipmentList', {
    tags: [tag],
  });
};

const saveTags = async () => {
  if (tagCancelToken.value) {
    saveTagsDebounced.cancel();
    tagCancelToken.value.cancel();
  }
  tagCancelToken.value = axios.CancelToken.source();

  try {
    tags.value.loading = true;
    await api.shipment.updateTags(
      shipment.value,
      internalTags.value,
      tagCancelToken.value,
    );
    shipment.value.tags = internalTags.value;
    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('tags.tags', 2),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving tags for order', e);
    tags.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.savingParam', { param: t('tags.tags', 2) }),
    });
  } finally {
    tags.value.loading = false;
  }
};
const saveTagsDebounced = debounce(saveTags, 1500);

const onAddTag = (tag: string) => {
  const newTag = new Tag({ name: tag });
  if (!tags.value.data?.includes(newTag)) {
    tags.value.data?.push(newTag);
  }
  saveTagsDebounced();
};

const onRemoveTag = (_tag: string) => {
  saveTagsDebounced();
};

const onWaterfallTender = async (payload: {
  configuration: WaterfallTenderConfig;
  callback: (success: boolean) => void;
}) => {
  try {
    saving.value = true;
    waterfallTender.value.loading = true;

    const response = await api.shipment.initiateWaterfallTender(
      shipmentId.value,
      payload.configuration,
    );

    shipment.value = response.shipment;
    waterfallTender.value.data = response.waterfallTender;

    loadRateShop();
    refreshWaterfallTenderUntilInProgress(5);
    reset(shipment.value);

    toast.addSuccess({
      detail: t('messages.successStartingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
    });
    payload.callback(true);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    payload.callback(false);
    console.error('Error initializing waterfall tender', e);
    toast.addError({
      detail: t('errors.startingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
      error: e,
    });
  } finally {
    saving.value = false;
    waterfallTender.value.loading = false;
    reloadShipmentFinancials();
  }
};

const refreshWaterfallTenderUntilInProgress = async (attempts: number) => {
  if (waterfallTender.value.data?.status === 'IN_PROGRESS' || attempts < 1) {
    return;
  }

  setTimeout(async () => {
    await loadWaterfallTender();
    refreshWaterfallTenderUntilInProgress(attempts - 1);
  }, 3000);
};

const onCancelWaterfallTender = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('common.cancelParam', {
      param: t('waterfallTender.waterfallTender'),
    }),
    message: t('waterfallTender.confirmCancelation'),
    accept: onConfirmCancelWaterfallTender,
    acceptClass: 'p-button-sm p-button-danger',
    acceptIcon: 'pi pi-check',
    acceptLabel: t('common.cancelParam', {
      param: t('waterfallTender.waterfallTender'),
    }),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.dontCancelParam', {
      param: t('waterfallTender.waterfallTender'),
    }),
  });
};

const onConfirmCancelWaterfallTender = async () => {
  try {
    const updatedShipment = await api.shipment.cancelActiveWaterfallTender(
      shipmentId.value,
    );
    shipment.value = updatedShipment;
    waterfallTender.value.data = undefined;
    loadRateShop();
    reset(shipment.value);

    toast.addSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('cancel waterfall tender error', shipmentId.value, e);
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
      error: e,
    });
  } finally {
    reloadShipmentFinancials();
  }
};

const startRemoveOrdersAction = () => {
  showRemoveOrdersModal.value = true;
};

const removeOrders = async (orderIds: number[]) => {
  const numOrders = relatedOrders.value.data?.length ?? 1;
  if (numOrders === orderIds.length) {
    cancelShipment();
    return;
  }

  try {
    saving.value = true;
    shipment.value = await api.shipment.removeOrders(shipment.value, orderIds);
    relatedOrders.value.data = relatedOrders.value.data?.filter((o) => {
      return !!o.id && !orderIds.includes(o.id);
    });
    reset(shipment.value);
    toast.addSuccess({
      detail: t('messages.successRemovingParam', {
        param: t('orders.orders', orderIds.length),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error removing orders from shipment', e);
    toast.addError({
      detail: t('errors.removingParam', {
        param: t('orders.orders', orderIds.length),
      }),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};

const onCargoInsurancePurchased = (certificate: CargoInsuranceCertificate) => {
  shipmentCargoInsuranceCertificate.value.data = certificate;
  reloadShipmentFinancials();
};

const onDelete = (tag: string) => {
  const tagToDelete = tags.value.data?.find((t: Tag) => t.name === tag);
  if (tagToDelete) {
    deleteTag(tagToDelete);
  }
};

const onLineItemRemoved = (removedLineItem: ShipmentLineItem) => {
  if (isShipmentLineItemRemoveShipOrderEnabled.value) {
    // Check if removedLineItem is the only line item with this orderId
    const matchingOrderItems = shipment.value.lineItems.filter(
      (item) => item.orderId === removedLineItem.orderId,
    );

    // If the line item to be removed is the only not the only one for the order we will split the shipOrder
    // Else the Order will be removed from the shipment
    const isShipOrderSplit = !(
      matchingOrderItems.length === 1 &&
      matchingOrderItems[0].id === removedLineItem.id
    );
    lineItemRemovedModal.value = {
      visible: true,
      isShipOrderSplit: isShipOrderSplit,
      removedLineItem: removedLineItem,
    };
  }
};

const onLineItemRemovalConfirmed = async (
  removedLineItem: ShipmentLineItem,
) => {
  if (!shipment.value.id || !removedLineItem.id) {
    return;
  }

  try {
    saving.value = true;
    await api.shipment.removeLineItem(shipment.value.id, removedLineItem.id);

    await loadShipment(shipmentId.value);
    toast.addSuccess({
      detail: t('messages.successRemovingParam', {
        param: t('lineItems.lineItems', 1),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error removing line item from shipment', e);
    toast.addError({
      detail: t('errors.removingParam', {
        param: t('lineItems.lineItems'),
      }),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};

watch(
  () => owningClient.value?.id,
  (newVal: number | undefined) => {
    if (newVal) {
      loadClientBranding(newVal);
      loadCarrierSettings();
    }
  },
);

watch(
  shipmentId,
  async (newVal: number) => {
    clearParentEntity();
    await loadShipment(newVal);
    loadAssignableUsers();

    internalTags.value = [...shipment.value.tags];
  },
  { immediate: true },
);

watch(
  () => shipment.value.scac,
  () => {
    loadCarrierSettings();
  },
);

watch(
  () => v$.value.lineItems,
  () => {
    if (!numDirtyLineItems.value) {
      v$.value.lineItems.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.referenceNumbers,
  () => {
    if (!numDirtyRefNums.value) {
      v$.value.referenceNumbers.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.requestedAccessorials,
  () => {
    if (!numDirtyAccessorials.value) {
      v$.value.requestedAccessorials.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => v$.value.stops,
  () => {
    if (!numDirtyStops.value) {
      v$.value.stops.$reset();
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => brokerageInfo.value.isShowingModal,
  (newVal) => {
    if (!newVal) {
      brokerageInfo.value.error = undefined;
    }
  },
);

onBeforeUnmount(() => {
  clearParentEntity();
});

provide('locationSectionId', LOCATION_SECTION_ID);
provide('shipmentStops', shipmentStops);
</script>

<style>
.shipment-detail {
  .shipment-detail-header {
    .shipment-detail-header-cell {
      border-right: var(--divider);
      padding: var(--p-card-body-padding);
      flex: 1 0 auto;
      align-items: center;

      &.shipment-health-cell {
        position: relative;

        .shipment-health-chip.popup-dropdown {
          position: static;
          font-size: 1rem;

          & > .popup-inputgroup-edit-icon {
            top: var(--sp-3);
            right: var(--sp-3);
          }

          & > .popup-inputgroup-card {
            top: var(--sp-3);
            left: var(--sp-8);
            width: max-content;
          }
        }
      }

      &:last-child {
        border-right: none;
      }

      .value {
        font-size: 18px;
      }
    }
  }

  .detail-section {
    &.related-ref-nums-section {
      border-top: none;
      padding-top: 0;
    }
  }
}

body.p-overflow-hidden-force {
  overflow: hidden;
  padding-right: var(--p-scrollbar-width);
}
</style>
